/* Le capteur de largeur d'écran — 03/09/2026
   ══════════════════════════════════════════════════════════════

   Toute la console est stylée en objets JavaScript posés directement
   sur les balises (`style={s.container}`). C'est lisible et c'est le
   parti pris du projet, mais un style inline ne peut PAS exprimer
   « si l'écran fait moins de 760 px » : les media queries n'existent
   pas dans ce format. C'est pour cette raison, et non par oubli, qu'il
   n'y avait aucune adaptation d'écran avant aujourd'hui.

   D'où ce capteur : il rend la largeur disponible sous forme d'un
   simple booléen, que les écrans consultent pour changer leur
   DISPOSITION — jamais leur apparence. Les couleurs, la typographie,
   les composants et les espacements restent ceux de `theme.js`, à
   l'identique. Sur un téléphone on doit reconnaître la même
   application, pas en découvrir une seconde.

   ── La règle de sûreté ───────────────────────────────────────────
   Tout ce qui dépend de ce capteur s'écrit ainsi :

       ...(mobile ? { ...adaptation } : {})
       mobile ? <VersionEtroite/> : <LeCodeDAvantIntact/>

   Quand `mobile` vaut faux — c'est-à-dire sur tout écran d'ordinateur —
   le résultat est rigoureusement celui d'avant. Pas « très proche » :
   identique, octet pour octet. Le fichier `rendu-bureau.test.js` le
   vérifie mécaniquement à chaque exécution.

   ── Pourquoi 760 px ──────────────────────────────────────────────
   Au-dessus, deux colonnes de contenu tiennent encore côte à côte et
   la barre d'onglets ne se replie pas. En dessous, elles se
   chevauchent. C'est la largeur d'une tablette en portrait ; les
   téléphones, eux, tournent entre 320 et 430 px. La même valeur est
   écrite dans `mobile.css`, et les deux DOIVENT rester d'accord —
   c'est le seul point de vigilance de ce fichier. */
import { useEffect, useState } from 'react';

export const SEUIL_MOBILE = 760;
const REQUETE = `(max-width: ${SEUIL_MOBILE}px)`;

/* Mesure hors de React, utilisable au premier rendu comme dans un
   gestionnaire d'événement. Renvoie `false` — donc « ordinateur » — dès
   que la mesure est impossible (rendu côté serveur, très vieux
   navigateur, environnement de test) : en cas de doute, on sert la
   version historique, jamais l'adaptation. */
export function estMobile() {
  try {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia(REQUETE).matches;
    }
    return window.innerWidth <= SEUIL_MOBILE;
  } catch (e) {
    return false;
  }
}

/* Le même renseignement, mais réactif : l'écran se redessine quand on
   pivote le téléphone ou qu'on redimensionne la fenêtre.

   On écoute `matchMedia` et non `resize` : le navigateur ne prévient
   qu'au FRANCHISSEMENT du seuil, pas à chaque pixel parcouru pendant
   qu'on étire une fenêtre. Un écouteur de `resize` déclencherait des
   dizaines de re-rendus pour un seul geste.

   `addListener` est conservé en repli : `addEventListener` sur un
   MediaQueryList n'existe pas sur Safari iOS avant la version 14, et
   c'est précisément le genre d'appareil qu'on cherche à servir. */
export function useMobile() {
  const [mobile, setMobile] = useState(estMobile);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(REQUETE);
    const surChangement = (e) => setMobile(e.matches);

    // On resynchronise à l'abonnement : entre le premier rendu et cet
    // effet, l'écran a pu changer (rotation pendant le chargement).
    setMobile(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', surChangement);
      return () => mql.removeEventListener('change', surChangement);
    }
    if (mql.addListener) {
      mql.addListener(surChangement);
      return () => mql.removeListener(surChangement);
    }
    return undefined;
  }, []);

  return mobile;
}

/* Empêche la page de défiler derrière un panneau ouvert.
   Sans cela, le doigt qui glisse sur le menu fait défiler la page en
   dessous — le défaut le plus courant des tiroirs de navigation, et
   celui qui donne le plus l'impression d'une application bâclée.

   La position de défilement est mémorisée puis restaurée : la simple
   pose d'un `overflow: hidden` sur le corps de page ramène l'écran en
   haut à la fermeture, et on perd sa place dans une longue liste. */
export function useDefilementBloque(actif) {
  useEffect(() => {
    if (!actif || typeof document === 'undefined') return undefined;
    const corps = document.body;
    const y = window.scrollY;
    const memoire = {
      position: corps.style.position,
      top: corps.style.top,
      width: corps.style.width,
      overflow: corps.style.overflow,
    };
    corps.style.position = 'fixed';
    corps.style.top = `-${y}px`;
    corps.style.width = '100%';
    corps.style.overflow = 'hidden';
    return () => {
      corps.style.position = memoire.position;
      corps.style.top = memoire.top;
      corps.style.width = memoire.width;
      corps.style.overflow = memoire.overflow;
      window.scrollTo(0, y);
    };
  }, [actif]);
}

/* Ferme un panneau avec la touche Échap.
   Attendu de tout élément qui recouvre la page : sans cette touche, un
   panneau dont le bouton de fermeture est sorti de l'écran devient un
   piège. */
export function useEchappe(actif, fermer) {
  useEffect(() => {
    if (!actif || typeof document === 'undefined') return undefined;
    const surTouche = (e) => { if (e.key === 'Escape') fermer(); };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [actif, fermer]);
}
